import {
  ArrowLeft,
  Check,
  ChevronRight,
  Copy,
  ExternalLink,
} from "lucide-react";
import { Button, Input, Spinner } from "@opencloud/ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import { personalAiAccountsAllowed, type AgentBootstrap } from "../bootstrap.js";
import { agentBrand } from "../brand.js";
import { AgentApiError, api } from "../api.js";
import { aiAccountName, organisationAiAccountsUrl, personalAiAccountsUrl } from "../app-ai.js";
import type {
  ProviderAuthMethod,
  ProviderAuthorizationView,
  ProviderConnectionView,
  ProviderView,
} from "../types.js";
import { AgentNotificationSettings } from "./AgentNotifications.js";

const ATTEMPT_KEY = "opencloud.providerAttempt";

interface ProviderMethodSelection {
  provider: ProviderView;
  method: ProviderAuthMethod;
  reconnectConnectionId?: string;
}

/**
 * Agent settings and the first-run connect step. AI accounts, and which one
 * pays for an app's work, are managed in the dashboard; settings only link
 * there. The connect step stays because Agent can't start without an
 * account where the installation provides no AI. Organisation installations
 * have no personal accounts, so there it only explains that an admin connects
 * one in the dashboard.
 */
export function ProviderSetup({
  bootstrap,
  screen = "settings",
  providers,
  connections,
  providerBlocked = null,
  onConnectionsChanged,
  onBack,
  notify,
  engineLabels,
  appAi,
}: {
  bootstrap: AgentBootstrap;
  /** `connect` is the first-run step: it opens on the provider choices. */
  screen?: "settings" | "connect";
  providers: readonly ProviderView[];
  connections: readonly ProviderConnectionView[];
  /** Why the app's latest run is paused for its AI account, if it is. */
  providerBlocked?: "reconnect_provider" | "wait" | null;
  onConnectionsChanged: () => Promise<void>;
  onBack: (() => void) | null;
  notify: (message: string) => void;
  /** Agent engine per provider, shown only when the harness makes it true. */
  engineLabels?: Partial<Record<string, string>> | undefined;
  /** The current app and its dashboard AI page; absent outside an app. */
  appAi?: { readonly appName: string; readonly dashboardUrl: string } | undefined;
}) {
  const connectMode = screen === "connect";
  const [attempt, setAttempt] = useState<ProviderAuthorizationView | null>(
    null,
  );
  const [startingKey, setStartingKey] = useState<string | null>(null);
  const [apiMethod, setApiMethod] = useState<{
    provider: ProviderView;
    method: ProviderAuthMethod;
    reconnectConnectionId?: string;
  } | null>(null);
  const [selectedMethod, setSelectedMethod] =
    useState<ProviderMethodSelection | null>(null);
  const [addingConnection, setAddingConnection] = useState(connectMode);
  const [progress, setProgress] = useState("");
  const [authorizationCode, setAuthorizationCode] = useState("");
  const [submittingCode, setSubmittingCode] = useState(false);
  const [cancellingAttempt, setCancellingAttempt] = useState(false);
  const visibleConnections = useMemo(
    () => latestConnections(connections),
    [connections],
  );

  const checkAttempt = useCallback(
    async (announce: boolean) => {
      const attemptId = localStorage.getItem(ATTEMPT_KEY);
      if (!attemptId) return;
      try {
        const value = await api<ProviderAuthorizationView>(
          `/v1/provider-authorizations/${encodeURIComponent(attemptId)}`,
        );
        if (value.status === "completed") {
          localStorage.removeItem(ATTEMPT_KEY);
          setAttempt(null);
          setSelectedMethod(null);
          if (!connectMode) setAddingConnection(false);
          setProgress("");
          await onConnectionsChanged();
          if (announce) notify(`${aiAccountName(value.connection.label)} connected.`);
          return;
        }
        if (authorizationAttemptFailed(value.status)) {
          localStorage.removeItem(ATTEMPT_KEY);
          setAttempt(null);
          setSelectedMethod(null);
          setProgress("That sign-in did not finish. You can try again.");
          await onConnectionsChanged();
          if (value.status === "cancelled" && !connectMode) setAddingConnection(false);
          return;
        }
        setAddingConnection(true);
        setAttempt(value);
      } catch {
        localStorage.removeItem(ATTEMPT_KEY);
        setAttempt(null);
      }
    },
    [connectMode, notify, onConnectionsChanged],
  );

  useEffect(() => {
    void checkAttempt(false);
  }, [checkAttempt]);

  useEffect(() => {
    if (!attempt) return;
    const timer = window.setInterval(() => void checkAttempt(false), 1_500);
    return () => window.clearInterval(timer);
  }, [attempt, checkAttempt]);

  async function start(provider: ProviderView, method: ProviderAuthMethod) {
    const key = `${provider.id}:${method.method}`;
    const failed = visibleConnections.find(
      (connection) =>
        connection.provider === provider.id &&
        connection.authMethod === method.method &&
        connection.status !== "connected",
    );
    setStartingKey(key);
    setProgress(`Starting secure ${method.label.toLowerCase()}…`);
    try {
      const value = await api<ProviderAuthorizationView>(
        `/v1/providers/${encodeURIComponent(provider.id)}/connections`,
        {
          method: "POST",
          body: JSON.stringify({
            authMethod: method.method,
            label: method.label,
            ...(failed ? { reconnectConnectionId: failed.id } : {}),
          }),
        },
      );
      if (method.method === "browser_oauth") {
        if (!value.authorizationUrl) {
          throw new Error("The provider did not return a sign-in URL.");
        }
        window.location.assign(value.authorizationUrl);
        return;
      }
      localStorage.setItem(ATTEMPT_KEY, value.attemptId);
      setAttempt(value);
      setSelectedMethod(null);
      setProgress("");
    } catch (error) {
      setProgress(
        error instanceof Error
          ? error.message
          : "The connection could not start.",
      );
    } finally {
      setStartingKey(null);
    }
  }

  function selectMethod(provider: ProviderView, method: ProviderAuthMethod) {
    const failed = visibleConnections.find(
      (connection) =>
        connection.provider === provider.id &&
        connection.authMethod === method.method &&
        connection.status !== "connected",
    );
    const selection = {
      provider,
      method,
      ...(failed ? { reconnectConnectionId: failed.id } : {}),
    };
    setProgress("");
    if (method.method === "api_key") {
      setSelectedMethod(null);
      setApiMethod(selection);
      return;
    }
    setApiMethod(null);
    setSelectedMethod(selection);
  }

  async function cancelAttempt() {
    const attemptId = localStorage.getItem(ATTEMPT_KEY);
    if (!attemptId) return;
    setCancellingAttempt(true);
    try {
      await api(
        `/v1/provider-authorizations/${encodeURIComponent(attemptId)}`,
        {
          method: "DELETE",
        },
      );
      localStorage.removeItem(ATTEMPT_KEY);
      setAttempt(null);
      setSelectedMethod(null);
      if (!connectMode) setAddingConnection(false);
      setProgress("Sign-in cancelled.");
      await onConnectionsChanged();
    } catch (error) {
      setProgress(
        error instanceof Error
          ? error.message
          : "The sign-in could not be cancelled.",
      );
    } finally {
      setCancellingAttempt(false);
    }
  }

  async function submitAuthorizationCode() {
    if (!attempt || attempt.method !== "manual_oauth") return;
    const secret = authorizationCode.trim();
    if (!secret) return;
    setAuthorizationCode("");
    setSubmittingCode(true);
    setProgress("Submitting the Claude authorization code…");
    try {
      await api(
        `/v1/provider-authorizations/${encodeURIComponent(attempt.attemptId)}/code`,
        {
          method: "POST",
          body: JSON.stringify({
            schemaVersion: 1,
            attemptId: attempt.attemptId,
            authorizationCode: secret,
          }),
        },
      );
      setProgress("Code accepted. Finishing Claude sign-in…");
      await checkAttempt(false);
    } catch (error) {
      setProgress(
        error instanceof Error
          ? error.message
          : "The Claude code was not accepted.",
      );
    } finally {
      setSubmittingCode(false);
    }
  }

  // Organisation installations: only admins connect accounts, in the dashboard.
  const organisationAi = !personalAiAccountsAllowed(bootstrap);
  const showingAddConnection = !organisationAi &&
    (addingConnection || Boolean(attempt || apiMethod || selectedMethod));
  const brand = agentBrand();
  const appContext = Boolean(appAi);

  return (
    <div
      className="a3-theme a3-setup-shell"
      data-oc-theme={agentBrand().themeId}
      data-design-system="opencloud-ui"
    >
      <main className="a3-setup">
        <div className="a3-setup-brand">
          <img
            src={bootstrap.logoUrl}
            alt=""
            aria-hidden="true"
            width="28"
            height="28"
          />
          {`${brand.productName} ${brand.agentName}`}
        </div>
        <section
          className="a3-setup-card"
          id={connectMode ? "provider-setup" : "agent-settings"}
          aria-busy={startingKey !== null || cancellingAttempt}
        >
          {connectMode && organisationAi ? (
            <>
              <div>
                <p className="a3-eyebrow">Get started</p>
                <h1>Your organisation hasn't connected AI yet</h1>
              </div>
              <p className="a3-setup-lede">
                An admin connects an AI account for your organisation in the dashboard. Agent works as soon as they do.
              </p>
              <Button
                nativeButton={false}
                render={<a href={bootstrap.appsUrl} />}
                className="a3-action a3-action-primary a3-action-large"
              >
                Open the dashboard
              </Button>
            </>
          ) : showingAddConnection ? (
            <>
              {!attempt && connectMode && (apiMethod || selectedMethod) ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="a3-back-link"
                  onClick={() => {
                    setApiMethod(null);
                    setSelectedMethod(null);
                    setProgress("");
                  }}
                >
                  <ArrowLeft aria-hidden="true" /> Back
                </Button>
              ) : !attempt && !connectMode ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="a3-back-link"
                  onClick={() => {
                    setAddingConnection(false);
                    setApiMethod(null);
                    setSelectedMethod(null);
                    setProgress("");
                  }}
                >
                  <ArrowLeft aria-hidden="true" />
                  Back to Agent settings
                </Button>
              ) : null}
              <div>
                <p className="a3-eyebrow">{connectMode ? "Get started" : "AI accounts"}</p>
                <h1>{connectMode ? "Connect your AI" : "Connect an AI account"}</h1>
              </div>
              <p className="a3-setup-lede">
                {connectMode
                  ? "Agent builds with your own AI account. Connect one to start building; you can add more later."
                  : "Connect ChatGPT, Claude or a provider API key. You can add more later."}
              </p>
              {progress ? (
                <p className="a3-setup-notice" role="status">
                  {progress}
                </p>
              ) : null}

              {attempt ? (
                <AuthorizationChallenge
                  attempt={attempt}
                  authorizationCode={authorizationCode}
                  submittingCode={submittingCode}
                  cancelling={cancellingAttempt}
                  onAuthorizationCodeChange={setAuthorizationCode}
                  onSubmitCode={() => void submitAuthorizationCode()}
                  onCancel={() => void cancelAttempt()}
                  onCopyCode={() =>
                    void navigator.clipboard
                      .writeText(attempt.userCode ?? "")
                      .then(() => notify("One-time code copied."))
                  }
                />
              ) : apiMethod ? (
                <ApiKeyForm
                  selection={apiMethod}
                  onCancel={() => setApiMethod(null)}
                  onConnected={async (connection) => {
                    setApiMethod(null);
                    if (!connectMode) setAddingConnection(false);
                    await onConnectionsChanged();
                    notify(`${aiAccountName(connection.label)} connected.`);
                  }}
                />
              ) : selectedMethod ? (
                <ProviderSignInStart
                  selection={selectedMethod}
                  busy={
                    startingKey ===
                    `${selectedMethod.provider.id}:${selectedMethod.method.method}`
                  }
                  onStart={() =>
                    void start(selectedMethod.provider, selectedMethod.method)
                  }
                  onChange={() => setSelectedMethod(null)}
                />
              ) : (
                <>
                  <ul className="a3-provider-choices">
                    {providerChoices(providers, visibleConnections).map((choice) => {
                      const primary = choice.signIn?.method.available
                        ? choice.signIn
                        : choice.apiKey?.method.available
                          ? choice.apiKey
                          : (choice.signIn ?? choice.apiKey);
                      const secondary =
                        primary === choice.signIn ? choice.apiKey : null;
                      const unavailable =
                        primary && !primary.method.available
                          ? primary.method.unavailableReason
                          : null;
                      const engine = engineLabels?.[choice.provider.id];
                      return (
                        <li
                          key={choice.provider.id}
                          className="a3-provider-choice provider-option"
                          data-brand={choice.brand}
                        >
                          <div className="a3-provider-choice-head">
                            <span
                              className="a3-method-mark"
                              data-brand={choice.brand}
                            >
                              <ProviderMark
                                provider={choice.provider.id}
                                authMethod={choice.signIn?.method.method ?? "api_key"}
                              />
                            </span>
                            <span className="a3-provider-choice-title">
                              <strong>{choice.title}</strong>
                              <small>
                                {engine
                                  ? `Runs Agent with ${engine}`
                                  : choice.provider.displayName}
                              </small>
                            </span>
                            {choice.connectedCount > 0 ? (
                              <span className="a3-provider-choice-badge">
                                Connected
                              </span>
                            ) : null}
                          </div>
                          {primary ? (
                            <Button
                              type="button"
                              className="a3-provider-choice-primary"
                              disabled={
                                !primary.method.available || startingKey !== null
                              }
                              aria-label={`Connect ${primary.method.label}`}
                              onClick={() =>
                                selectMethod(primary.provider, primary.method)
                              }
                            >
                              {primary.method.label}
                            </Button>
                          ) : null}
                          <p className="a3-provider-choice-note">
                            {unavailable ??
                              (primary === choice.signIn
                                ? choice.planNote
                                : "Usage-based billing with your provider.")}
                          </p>
                          {secondary ? (
                            <Button
                              type="button"
                              variant="link"
                              className="a3-provider-choice-secondary"
                              disabled={
                                !secondary.method.available || startingKey !== null
                              }
                              aria-label={`Connect ${secondary.method.label}`}
                              onClick={() =>
                                selectMethod(secondary.provider, secondary.method)
                              }
                            >
                              Use an API key instead
                              <ChevronRight aria-hidden="true" />
                            </Button>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                  <p className="a3-setup-quiet a3-provider-choices-footer">
                    Sign-in tokens and API keys are encrypted and never exposed
                    to app code.
                  </p>
                </>
              )}
            </>
          ) : (
            <>
              {onBack ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="a3-back-link"
                  onClick={onBack}
                >
                  <ArrowLeft aria-hidden="true" /> Back
                </Button>
              ) : null}
              <div>
                <p className="a3-eyebrow">Agent</p>
                <h1>Agent settings</h1>
              </div>
              <p className="a3-setup-lede">
                {appContext
                  ? "AI for this app and completion notifications."
                  : "AI accounts and completion notifications."}
              </p>
              {providerBlocked ? (
                <p className="a3-setup-notice" role="status">
                  {providerBlocked === "reconnect_provider"
                    ? organisationAi
                      ? "This app’s work is paused until its AI account is reconnected. Ask an admin to reconnect it; the work then continues from where it stopped."
                      : "This app’s work is paused until its AI account is reconnected. Reconnect that account and the work continues from where it stopped."
                    : "This app’s work is paused while its AI account is at a provider limit. It tries again by itself and continues once the limit resets."}{" "}
                  Its conversation and progress are kept.
                </p>
              ) : null}
              {progress ? (
                <p className="a3-setup-notice" role="status">
                  {progress}
                </p>
              ) : null}
              <div className="a3-agent-settings-list">
                <section
                  className="a3-agent-settings-card a3-your-accounts"
                  aria-labelledby="agent-ai-settings"
                >
                  <h2 id="agent-ai-settings">AI</h2>
                  <p>
                    {appAi
                      ? `Pick each chat's model in the composer; its provider pays. ${appAi.appName}'s automatic tasks are set on its AI page in the dashboard.`
                      : organisationAi
                        ? "Your organisation's AI providers pay for the models you pick. Admins manage them in the dashboard."
                        : "Your AI providers and their models are managed in the dashboard."}
                  </p>
                  <div className="a3-agent-settings-actions">
                    <Button
                      variant="outline"
                      className="a3-action a3-action-secondary"
                      nativeButton={false}
                      render={<a href={appAi?.dashboardUrl ?? (organisationAi
                        ? organisationAiAccountsUrl(bootstrap.appsUrl)
                        : personalAiAccountsUrl(bootstrap.appsUrl))} />}
                    >
                      {appAi ? "Open AI settings" : organisationAi ? "Open AI providers" : "Manage AI providers"}
                      <ExternalLink aria-hidden="true" />
                    </Button>
                  </div>
                </section>
                <AgentNotificationSettings notify={notify} />
              </div>
              {onBack ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="a3-action a3-action-quiet"
                  onClick={onBack}
                >
                  <Check aria-hidden="true" /> Done
                </Button>
              ) : null}
            </>
          )}
        </section>
        {connectMode ? (
          // Connecting is required, so the only ways out leave Agent.
          <nav className="a3-setup-exit" aria-label="Leave setup">
            <Button
              variant="ghost"
              className="a3-action a3-action-quiet"
              nativeButton={false}
              render={<a href={bootstrap.appsUrl} />}
            >
              My apps
            </Button>
            <form method="post" action={bootstrap.signOutUrl}>
              <Button type="submit" variant="ghost" className="a3-action a3-action-quiet">
                Sign out
              </Button>
            </form>
          </nav>
        ) : null}
      </main>
    </div>
  );
}

export function authorizationAttemptFailed(
  status: ProviderAuthorizationView["status"],
): boolean {
  return ["denied", "failed", "cancelled", "expired"].includes(
    status ?? "pending",
  );
}

function AuthorizationChallenge({
  attempt,
  authorizationCode,
  submittingCode,
  cancelling,
  onAuthorizationCodeChange,
  onSubmitCode,
  onCancel,
  onCopyCode,
}: {
  attempt: ProviderAuthorizationView;
  authorizationCode: string;
  submittingCode: boolean;
  cancelling: boolean;
  onAuthorizationCodeChange: (value: string) => void;
  onSubmitCode: () => void;
  onCancel: () => void;
  onCopyCode: () => void;
}) {
  if (
    attempt.status === "exchanging" ||
    !attempt.authorizationUrl ||
    !attempt.instructions ||
    (attempt.method === "device_oauth" && !attempt.userCode)
  ) {
    return (
      <div className="a3-provider-auth" id="device-challenge" role="status">
        <Spinner aria-label="Finishing provider sign-in" />
        <p>Finishing secure provider sign-in…</p>
      </div>
    );
  }
  if (attempt.method === "manual_oauth") {
    return (
      <div className="a3-provider-auth" id="device-challenge">
        <div>
          <h2>Complete Claude sign-in</h2>
          <p className="a3-setup-quiet">
            Open Claude first, approve access, copy the authorization code
            Claude shows, then paste it back below.
          </p>
        </div>
        <div className="a3-provider-auth-steps">
          <div className="a3-provider-auth-step">
            <span className="a3-provider-auth-step-number" aria-hidden="true">
              1
            </span>
            <div className="a3-provider-auth-step-content">
              <strong>Open Claude and get your code</strong>
              <p>
                Sign in on Claude’s official page and approve access. When
                Claude finishes, copy the authorization code it gives you.
              </p>
              <Button
                render={
                  <a
                    href={attempt.authorizationUrl}
                    target="_blank"
                    rel="noreferrer"
                  />
                }
                className="a3-action a3-action-primary"
              >
                <ExternalLink aria-hidden="true" /> Open Claude sign-in
              </Button>
            </div>
          </div>
          <form
            className="a3-provider-auth-step"
            onSubmit={(event) => {
              event.preventDefault();
              onSubmitCode();
            }}
          >
            <span className="a3-provider-auth-step-number" aria-hidden="true">
              2
            </span>
            <div className="a3-provider-auth-step-content">
              <label className="a3-field">
                <span>Paste the code from Claude</span>
                <Input
                  name="authorizationCode"
                  type="password"
                  required
                  minLength={3}
                  maxLength={10_000}
                  autoComplete="off"
                  spellCheck={false}
                  value={authorizationCode}
                  placeholder="Paste the authorization code here"
                  onChange={(event) =>
                    onAuthorizationCodeChange(event.target.value)
                  }
                />
                <small>
                  {`The code is sent once to Claude to finish connecting and is never stored by ${agentBrand().productName}.`}
                </small>
              </label>
              <div className="a3-actions">
                <Button
                  type="submit"
                  className="a3-action a3-action-primary"
                  disabled={
                    submittingCode || cancelling || !authorizationCode.trim()
                  }
                >
                  {submittingCode ? (
                    <>
                      <Spinner aria-label="Connecting Claude" />{" "}
                      Connecting Claude…
                    </>
                  ) : (
                    "Connect Claude"
                  )}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="a3-action a3-action-quiet"
                  disabled={submittingCode || cancelling}
                  onClick={onCancel}
                >
                  {cancelling ? (
                    <>
                      <Spinner aria-label="Cancelling sign-in" />{" "}
                      Cancelling sign-in…
                    </>
                  ) : (
                    "Cancel sign-in"
                  )}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="a3-provider-auth" id="device-challenge">
      <div>
        <h2>Complete ChatGPT sign-in</h2>
        <p className="a3-setup-quiet">
          Copy the one-time code, open ChatGPT’s official sign-in page, and
          enter the code there.
        </p>
      </div>
      <div className="a3-provider-auth-steps">
        <div className="a3-provider-auth-step">
          <span className="a3-provider-auth-step-number" aria-hidden="true">
            1
          </span>
          <div className="a3-provider-auth-step-content">
            <strong>Copy your one-time code</strong>
            <p>
              This code identifies this sign-in. It expires automatically and
              cannot be reused.
            </p>
            <code className="a3-device-code">{attempt.userCode}</code>
            <Button
              type="button"
              variant="outline"
              className="a3-action a3-action-secondary"
              onClick={onCopyCode}
            >
              <Copy aria-hidden="true" /> Copy one-time code
            </Button>
          </div>
        </div>
        <div className="a3-provider-auth-step">
          <span className="a3-provider-auth-step-number" aria-hidden="true">
            2
          </span>
          <div className="a3-provider-auth-step-content">
            <strong>Open ChatGPT and enter the code</strong>
            <p>
              {`Open the secure provider page, sign in to ChatGPT, then enter the code from step 1. ${agentBrand().productName} checks completion automatically.`}
            </p>
            <Button
              render={
                <a
                  href={attempt.authorizationUrl}
                  target="_blank"
                  rel="noreferrer"
                />
              }
              className="a3-action a3-action-primary"
            >
              <ExternalLink aria-hidden="true" /> Open ChatGPT sign-in
            </Button>
            <div className="a3-actions">
              <Button
                type="button"
                variant="ghost"
                className="a3-action a3-action-quiet"
                disabled={cancelling}
                onClick={onCancel}
              >
                {cancelling ? (
                  <>
                    <Spinner aria-label="Cancelling sign-in" />{" "}
                    Cancelling sign-in…
                  </>
                ) : (
                  "Cancel sign-in"
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProviderSignInStart({
  selection,
  busy,
  onStart,
  onChange,
}: {
  selection: ProviderMethodSelection;
  busy: boolean;
  onStart: () => void;
  onChange: () => void;
}) {
  const claude = selection.provider.id === "anthropic";
  return (
    <div className="a3-connection-method-step">
      <strong>{claude ? "Sign in with Claude" : "Sign in with ChatGPT"}</strong>
      <p className="a3-setup-quiet">
        {claude
          ? `${agentBrand().productName} will prepare a secure Claude sign-in link. You’ll open that link, copy the code Claude gives you, and paste it back here.`
          : `Use Codex device authorization with an eligible ChatGPT plan. ${agentBrand().productName} never receives your password.`}
      </p>
      <div className="a3-actions">
        <Button
          type="button"
          className="a3-action a3-action-primary"
          disabled={busy}
          onClick={onStart}
        >
          {busy ? (
            <>
              <Spinner aria-label="Starting provider sign-in" />
              {claude
                ? "Starting Claude sign-in…"
                : "Starting ChatGPT sign-in…"}
            </>
          ) : claude ? (
            "Start Claude sign-in"
          ) : (
            "Start ChatGPT sign-in"
          )}
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="a3-action a3-action-quiet"
          onClick={onChange}
        >
          Choose another method
        </Button>
      </div>
    </div>
  );
}

function ApiKeyForm({
  selection,
  onCancel,
  onConnected,
}: {
  selection: ProviderMethodSelection;
  onCancel: () => void;
  onConnected: (connection: ProviderConnectionView) => Promise<void>;
}) {
  const [apiKey, setApiKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  return (
    <form
      className="a3-provider-api-form"
      onSubmit={(event) => {
        event.preventDefault();
        const secret = apiKey;
        setApiKey("");
        setBusy(true);
        setError("");
        void api<{ connection: ProviderConnectionView }>(
          `/v1/providers/${encodeURIComponent(selection.provider.id)}/connections`,
          {
            method: "POST",
            body: JSON.stringify({
              authMethod: "api_key",
              apiKey: secret,
              label: selection.method.label,
              ...(selection.reconnectConnectionId
                ? { reconnectConnectionId: selection.reconnectConnectionId }
                : {}),
            }),
          },
        )
          .then(({ connection }) => onConnected(connection))
          .catch((reason: unknown) => {
            setError(
              reason instanceof AgentApiError
                ? reason.message
                : "The API key was not connected.",
            );
          })
          .finally(() => setBusy(false));
      }}
    >
      <strong>{selection.provider.displayName} API key</strong>
      <label className="a3-field">
        <span>{selection.provider.displayName} API key</span>
        <Input
          name="apiKey"
          type="password"
          required
          minLength={8}
          maxLength={100_000}
          autoComplete="off"
          spellCheck={false}
          value={apiKey}
          placeholder="sk-…"
          onChange={(event) => setApiKey(event.target.value)}
        />
      </label>
      <p className="a3-setup-quiet">
        The key is submitted once over HTTPS, encrypted at rest, and never shown
        again or exposed to app code.
      </p>
      {error ? (
        <p className="a3-setup-notice" role="alert">
          {error}
        </p>
      ) : null}
      <div className="a3-actions">
        <Button
          type="submit"
          className="a3-action a3-action-primary"
          disabled={busy || apiKey.length < 8}
        >
          {busy ? (
            <>
              <Spinner aria-label="Connecting API key" /> Connecting
              API key…
            </>
          ) : (
            "Connect API key"
          )}
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="a3-action a3-action-quiet"
          onClick={onCancel}
        >
          Choose another method
        </Button>
      </div>
    </form>
  );
}

export interface OrderedProviderMethod extends ProviderMethodSelection {
  meta: string;
  description: string;
}

export function orderedProviderMethods(
  providers: readonly ProviderView[],
): OrderedProviderMethod[] {
  const { productName } = agentBrand();
  const order = [
    {
      providerId: "openai",
      method: "device_oauth",
      meta: "OpenAI · Account sign-in",
      description:
        `Use an eligible ChatGPT plan. You’ll open ChatGPT and enter a one-time code supplied by ${productName}.`,
    },
    {
      providerId: "anthropic",
      method: "manual_oauth",
      meta: "Anthropic · Account sign-in",
      description:
        `Open Claude, approve access, copy the authorization code it returns, and paste that code back into ${productName}.`,
    },
    {
      providerId: "openai",
      method: "api_key",
      meta: "OpenAI · Usage-based API billing",
      description:
        `Connect a metered OpenAI API key. ${productName} encrypts the key and never exposes it to app code.`,
    },
    {
      providerId: "anthropic",
      method: "api_key",
      meta: "Anthropic · Usage-based API billing",
      description:
        `Connect a metered Anthropic API key. ${productName} encrypts the key and never exposes it to app code.`,
    },
  ] as const satisfies readonly {
    providerId: string;
    method: ProviderAuthMethod["method"];
    meta: string;
    description: string;
  }[];

  return order.flatMap((presentation) => {
    const provider = providers.find(
      (candidate) => candidate.id === presentation.providerId,
    );
    const method = provider?.authMethods.find(
      (candidate) => candidate.method === presentation.method,
    );
    return provider && method
      ? [
          {
            provider,
            method,
            meta: presentation.meta,
            description: presentation.description,
          },
        ]
      : [];
  });
}

export interface ProviderChoice {
  provider: ProviderView;
  brand: ProviderBrand;
  title: string;
  planNote: string;
  signIn: OrderedProviderMethod | null;
  apiKey: OrderedProviderMethod | null;
  connectedCount: number;
}

/** Groups connection methods by provider: plan sign-in first, API key second. */
export function providerChoices(
  providers: readonly ProviderView[],
  connections: readonly ProviderConnectionView[],
): ProviderChoice[] {
  // Offer only the sign-in methods this installation enables.
  const methods = orderedProviderMethods(providers).filter(
    (method) => method.method.available,
  );
  const order = ["anthropic", "openai"];
  return providers
    .filter((provider) =>
      methods.some((method) => method.provider.id === provider.id),
    )
    .sort(
      (left, right) =>
        (order.indexOf(left.id) + 1 || order.length + 1) -
        (order.indexOf(right.id) + 1 || order.length + 1),
    )
    .map((provider) => {
      const own = methods.filter((method) => method.provider.id === provider.id);
      const claude = provider.id === "anthropic";
      return {
        provider,
        brand: claude ? "claude" : "chatgpt",
        title: claude ? "Claude" : provider.id === "openai" ? "ChatGPT" : provider.displayName,
        planNote: claude
          ? "Uses your Claude subscription."
          : "Uses your ChatGPT subscription.",
        signIn: own.find((method) => method.method.method !== "api_key") ?? null,
        apiKey: own.find((method) => method.method.method === "api_key") ?? null,
        connectedCount: connections.filter(
          (connection) =>
            connection.provider === provider.id &&
            connection.status === "connected",
        ).length,
      };
    });
}

export type ProviderBrand = "chatgpt" | "claude" | "openai" | "anthropic";

export function providerBrand(
  provider: string,
  authMethod: string,
): ProviderBrand {
  if (provider === "anthropic") {
    return authMethod === "api_key" ? "anthropic" : "claude";
  }
  return authMethod === "api_key" ? "openai" : "chatgpt";
}

function ProviderMark({
  provider,
  authMethod,
}: {
  provider: string;
  authMethod: string;
}) {
  const brand = providerBrand(provider, authMethod);
  const path =
    brand === "chatgpt"
      ? CHATGPT_MARK_PATH
      : brand === "claude"
        ? CLAUDE_MARK_PATH
        : brand === "anthropic"
          ? ANTHROPIC_MARK_PATH
          : OPENAI_MARK_PATH;
  return (
    <svg
      className="a3-provider-mark"
      data-brand={brand}
      fill="currentColor"
      fillRule="evenodd"
      viewBox={brand === "chatgpt" ? "0 0 512 512" : "0 0 24 24"}
      aria-hidden="true"
    >
      <path d={path} />
    </svg>
  );
}

const CHATGPT_MARK_PATH =
  "M132.4 291.9c-18.9-11-31.9-35-31.9-58 0-27.5 16.8-52.2 42.2-61.8v79.6c0 4.5 1.7 7.6 5.8 10l87.5 50.8-30.9 17.8c-1.7 1-4.5 1-6.2 0zm31.6-42.2 30.6 18.2V166.6c0-4.5 1.7-7.6 5.8-10l69-40.2c-11.7-9.3-26.1-14.4-41.2-14.4-38.8 0-67.3 28.1-67.3 65.6v76.5c0 2.5.7 4.2 3.1 5.6m48.8 29.1 41.5 23.3 41.2-23.3v-49.4l-41.2-23.3-41.5 23.3zm0-105v35.7l87.5-51.1c4.1-2.4 7.9-2.1 11.3 0l69.3 40.5c.7-3.1.7-6.9.7-9.6 0-34.7-26.4-66.9-68.3-66.9-11 0-22.3 2.4-31.2 7.6l-66.2 38.4c-2.5 1.3-3.1 3-3.1 5.4M512 256c0 141.4-114.6 256-256 256S0 397.4 0 256 114.6 0 256 0s256 114.6 256 256m-85.7 18.4c0-24-10.3-47-29.2-63.8 1.7-7.2 3.1-14.8 3.1-22 0-48.4-39.5-84.8-84.8-84.8-9.3 0-18.5 1.7-27.5 4.8-15.4-15.4-36.4-25.1-59.7-25.1-41.2 0-75.5 29.5-83.1 68.7-38.4 9.6-63.2 45.3-63.2 81.7 0 24 10.3 47 29.2 63.8-1.7 7.2-3.1 14.8-3.1 22 0 48.4 39.5 84.8 84.8 84.8 9.3 0 18.5-1.7 27.5-4.8 15.4 15.4 36.4 25.1 59.7 25.1 41.2 0 75.5-29.5 83.1-68.7 38.4-9.6 63.2-45.3 63.2-81.7m-50.5-58L309.2 178c-1.7-1-4.5-1-6.2 0l-30.9 17.8 87.9 51.1c4.1 2.4 5.8 5.5 5.8 10v79.3c25.4-9.6 41.9-34.7 41.9-61.8 0-23-13-47.1-31.9-58m-80.3 118v-35.7L208 349.5c-4.1 2.4-7.6 2.4-11.7 0l-69-39.8c-.3 3.1-.7 6.5-.7 10 0 36.7 28.8 66.2 68.3 66.2 11 0 22.3-2.4 31.2-7.6l66.2-38.4c2.5-1.3 3.2-3.1 3.2-5.5m49.1-75.8-30.9-18.2v101.3c0 4.5-1.7 7.6-5.8 10l-69 40.2c11.7 9.3 26.1 14.4 41.2 14.4 38.8 0 67.6-28.1 67.6-65.6v-76.5c0-2.5-.7-4.2-3.1-5.6";

const ANTHROPIC_MARK_PATH =
  "M17.3041 3.541h-3.6718l6.696 16.918H24Zm-10.6082 0L0 20.459h3.7442l1.3693-3.5527h7.0052l1.3693 3.5528h3.7442L10.5363 3.5409Zm-.3712 10.2232 2.2914-5.9456 2.2914 5.9456Z";

const CLAUDE_MARK_PATH =
  "M4.709 15.955l4.72-2.647.08-.23-.08-.128H9.2l-.79-.048-2.698-.073-2.339-.097-2.266-.122-.571-.121L0 11.784l.055-.352.48-.321.686.06 1.52.103 2.278.158 1.652.097 2.449.255h.389l.055-.157-.134-.098-.103-.097-2.358-1.596-2.552-1.688-1.336-.972-.724-.491-.364-.462-.158-1.008.656-.722.881.06.225.061.893.686 1.908 1.476 2.491 1.833.365.304.145-.103.019-.073-.164-.274-1.355-2.446-1.446-2.49-.644-1.032-.17-.619a2.97 2.97 0 01-.104-.729L6.283.134 6.696 0l.996.134.42.364.62 1.414 1.002 2.229 1.555 3.03.456.898.243.832.091.255h.158V9.01l.128-1.706.237-2.095.23-2.695.08-.76.376-.91.747-.492.584.28.48.685-.067.444-.286 1.851-.559 2.903-.364 1.942h.212l.243-.242.985-1.306 1.652-2.064.73-.82.85-.904.547-.431h1.033l.76 1.129-.34 1.166-1.064 1.347-.881 1.142-1.264 1.7-.79 1.36.073.11.188-.02 2.856-.606 1.543-.28 1.841-.315.833.388.091.395-.328.807-1.969.486-2.309.462-3.439.813-.042.03.049.061 1.549.146.662.036h1.622l3.02.225.79.522.474.638-.079.485-1.215.62-1.64-.389-3.829-.91-1.312-.329h-.182v.11l1.093 1.068 2.006 1.81 2.509 2.33.127.578-.322.455-.34-.049-2.205-1.657-.851-.747-1.926-1.62h-.128v.17l.444.649 2.345 3.521.122 1.08-.17.353-.608.213-.668-.122-1.374-1.925-1.415-2.167-1.143-1.943-.14.08-.674 7.254-.316.37-.729.28-.607-.461-.322-.747.322-1.476.389-1.924.315-1.53.286-1.9.17-.632-.012-.042-.14.018-1.434 1.967-2.18 2.945-1.726 1.845-.414.164-.717-.37.067-.662.401-.589 2.388-3.036 1.44-1.882.93-1.086-.006-.158h-.055L4.132 18.56l-1.13.146-.487-.456.061-.746.231-.243 1.908-1.312-.006.006z";

const OPENAI_MARK_PATH =
  "M9.205 8.658v-2.26c0-.19.072-.333.238-.428l4.543-2.616c.619-.357 1.356-.523 2.117-.523 2.854 0 4.662 2.212 4.662 4.566 0 .167 0 .357-.024.547l-4.71-2.759a.797.797 0 00-.856 0l-5.97 3.473zm10.609 8.8V12.06c0-.333-.143-.57-.429-.737l-5.97-3.473 1.95-1.118a.433.433 0 01.476 0l4.543 2.617c1.309.76 2.189 2.378 2.189 3.948 0 1.808-1.07 3.473-2.76 4.163zM7.802 12.703l-1.95-1.142c-.167-.095-.239-.238-.239-.428V5.899c0-2.545 1.95-4.472 4.591-4.472 1 0 1.927.333 2.712.928L8.23 5.067c-.285.166-.428.404-.428.737v6.898zM12 15.128l-2.795-1.57v-3.33L12 8.658l2.795 1.57v3.33L12 15.128zm1.796 7.23c-1 0-1.927-.332-2.712-.927l4.686-2.712c.285-.166.428-.404.428-.737v-6.898l1.974 1.142c.167.095.238.238.238.428v5.233c0 2.545-1.974 4.472-4.614 4.472zm-5.637-5.303l-4.544-2.617c-1.308-.761-2.188-2.378-2.188-3.948A4.482 4.482 0 014.21 6.327v5.423c0 .333.143.571.428.738l5.947 3.449-1.95 1.118a.432.432 0 01-.476 0zm-.262 3.9c-2.688 0-4.662-2.021-4.662-4.519 0-.19.024-.38.047-.57l4.686 2.71c.286.167.571.167.856 0l5.97-3.448v2.26c0 .19-.07.333-.237.428l-4.543 2.616c-.619.357-1.356.523-2.117.523zm5.899 2.83a5.947 5.947 0 005.827-4.756C22.287 18.339 24 15.84 24 13.296c0-1.665-.713-3.282-1.998-4.448.119-.5.19-.999.19-1.498 0-3.401-2.759-5.947-5.946-5.947-.642 0-1.26.095-1.88.31A5.962 5.962 0 0010.205 0a5.947 5.947 0 00-5.827 4.757C1.713 5.447 0 7.945 0 10.49c0 1.666.713 3.283 1.998 4.448-.119.5-.19 1-.19 1.499 0 3.401 2.759 5.946 5.946 5.946.642 0 1.26-.095 1.88-.309a5.96 5.96 0 004.162 1.713z";

function latestConnections(
  connections: readonly ProviderConnectionView[],
): ProviderConnectionView[] {
  const connected: ProviderConnectionView[] = [];
  const latestInactive = new Map<string, ProviderConnectionView>();
  for (const connection of connections) {
    if (connection.status === "revoked") continue;
    if (connection.status === "connected") {
      connected.push(connection);
      continue;
    }
    const key = `${connection.provider}:${connection.authMethod}`;
    if (!latestInactive.has(key)) latestInactive.set(key, connection);
  }
  return [...connected, ...latestInactive.values()].sort(
    (left, right) =>
      Number(right.isDefault) - Number(left.isDefault) ||
      left.label.localeCompare(right.label),
  );
}
